import React from 'react';
import { useAtom } from 'jotai';
import { Modal, Button, Card, Row, Col, Tag } from 'antd';
import { 
  LinkedinOutlined, 
  InstagramOutlined, 
  YoutubeOutlined,
  TwitterOutlined,
  CheckCircleOutlined 
} from '@ant-design/icons';
import { Video } from 'lucide-react';
import {
  isExportModalVisibleAtom,
  selectedPlatformAtom,
  exportVideoBlobsAtom
} from '../../atoms/exportAtoms';
import { PLATFORM_SPECS, PlatformType, getExportService } from '../../services/exportService';
import './PlatformExportSelector.scss';

interface PlatformExportSelectorProps {
  videoBlob: Blob;
  onExportStart?: (exportId: string) => void;
  onClose?: () => void;
}

const PLATFORM_INFO = {
  linkedin: {
    icon: <LinkedinOutlined />,
    name: 'LinkedIn',
    description: 'Square format (1:1) perfect for LinkedIn feeds',
    color: '#0077b5'
  },
  instagram: {
    icon: <InstagramOutlined />,
    name: 'Instagram',
    description: 'Vertical format (9:16) optimized for Instagram Reels',
    color: '#E4405F'
  },
  youtube: {
    icon: <YoutubeOutlined />,
    name: 'YouTube',
    description: 'Widescreen format (16:9) ideal for YouTube',
    color: '#FF0000'
  },
  twitter: {
    icon: <TwitterOutlined />,
    name: 'Twitter',
    description: 'Widescreen format (16:9) for Twitter/X posts',
    color: '#1DA1F2'
  },
  tiktok: {
    icon: <Video size={24} />,
    name: 'TikTok',
    description: 'Vertical format (9:16) for TikTok videos',
    color: '#000000'
  }
};

export const PlatformExportSelector: React.FC<PlatformExportSelectorProps> = ({
  videoBlob,
  onExportStart,
  onClose
}) => {
  const [isVisible, setIsVisible] = useAtom(isExportModalVisibleAtom);
  const [selectedPlatform, setSelectedPlatform] = useAtom(selectedPlatformAtom);
  const [, setVideoBlobs] = useAtom(exportVideoBlobsAtom);
  const [isExporting, setIsExporting] = React.useState(false);

  const exportService = getExportService();

  const handlePlatformSelect = (platform: PlatformType) => {
    setSelectedPlatform(platform);
  };

  const handleExport = async () => {
    if (!videoBlob || selectedPlatform === 'custom') return;

    setIsExporting(true);

    try {
      const exportId = exportService.queueExport({
        platform: selectedPlatform,
        videoBlobs: [videoBlob],
        filename: `${selectedPlatform}-export-${Date.now()}.mp4`
      });

      if (onExportStart) {
        onExportStart(exportId);
      }

      handleClose();
    } catch (error) {
      console.error('[PlatformExport] Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  const selectedSpec = PLATFORM_SPECS[selectedPlatform];

  return (
    <Modal
      title="Export for Social Media"
      open={isVisible}
      onCancel={handleClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          onClick={handleExport}
          loading={isExporting}
          disabled={selectedPlatform === 'custom'}
        >
          Export for {PLATFORM_INFO[selectedPlatform as keyof typeof PLATFORM_INFO]?.name || 'Platform'}
        </Button>
      ]}
      className="platform-export-selector-modal"
    >
      <div className="platform-export-selector">
        {/* Platform Cards */}
        <div className="platforms-section">
          <h3>Choose Platform</h3>
          <Row gutter={[16, 16]}>
            {(Object.keys(PLATFORM_INFO) as Array<keyof typeof PLATFORM_INFO>).map((platform) => {
              const info = PLATFORM_INFO[platform];
              const spec = PLATFORM_SPECS[platform];
              const isSelected = selectedPlatform === platform;

              return (
                <Col span={6} key={platform}>
                  <Card
                    className={`platform-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handlePlatformSelect(platform)}
                    hoverable
                  >
                    {isSelected && (
                      <div className="selected-indicator">
                        <CheckCircleOutlined />
                      </div>
                    )}
                    <div className="platform-icon" style={{ color: info.color }}>
                      {info.icon}
                    </div>
                    <h4>{info.name}</h4>
                    <p className="platform-description">{info.description}</p>
                    <div className="platform-specs">
                      <Tag>{spec.aspectRatio}</Tag>
                      <Tag>{spec.resolution.width}x{spec.resolution.height}</Tag>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Preview & Specs */}
        {selectedPlatform && selectedPlatform !== 'custom' && (
          <div className="preview-section">
            <h3>Export Settings</h3>
            <div className="specs-grid">
              <div className="spec-item">
                <label>Resolution</label>
                <span>{selectedSpec.resolution.width} × {selectedSpec.resolution.height}</span>
              </div>
              <div className="spec-item">
                <label>Aspect Ratio</label>
                <span>{selectedSpec.aspectRatio}</span>
              </div>
              <div className="spec-item">
                <label>Frame Rate</label>
                <span>{selectedSpec.frameRate} FPS</span>
              </div>
              <div className="spec-item">
                <label>Max Duration</label>
                <span>{selectedSpec.maxDuration ? `${selectedSpec.maxDuration}s` : 'Unlimited'}</span>
              </div>
              <div className="spec-item">
                <label>Video Codec</label>
                <span>{selectedSpec.codec.video}</span>
              </div>
              <div className="spec-item">
                <label>Audio Codec</label>
                <span>{selectedSpec.codec.audio}</span>
              </div>
            </div>

            {/* Aspect Ratio Preview */}
            <div className="aspect-ratio-preview">
              <div className="preview-label">Aspect Ratio Preview</div>
              <div className="preview-container">
                <div 
                  className={`preview-box ${selectedPlatform}`}
                  style={{
                    aspectRatio: selectedSpec.aspectRatio.replace(':', '/')
                  }}
                >
                  <div className="preview-content">
                    {selectedSpec.aspectRatio}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
